import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp, faArrowDown, faMinus } from "@fortawesome/free-solid-svg-icons";

const AnimatedKPICard = ({
  title,
  value,
  previousValue,
  icon,
  prefix = "",
  suffix = "",
  format = "number",
  color = "blue",
  className = "",
  animationDuration = 2000,
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  // Color schemes for different variants
  const colorSchemes = {
    blue: {
      gradient: "from-blue-500 to-blue-600",
      background: "bg-blue-50",
      icon: "text-blue-600",
      text: "text-blue-900",
      change: {
        positive: "text-emerald-600 bg-emerald-50",
        negative: "text-red-600 bg-red-50",
        neutral: "text-gray-600 bg-gray-50"
      }
    },
    green: {
      gradient: "from-emerald-500 to-emerald-600",
      background: "bg-emerald-50",
      icon: "text-emerald-600",
      text: "text-emerald-900",
      change: {
        positive: "text-emerald-600 bg-emerald-50",
        negative: "text-red-600 bg-red-50",
        neutral: "text-gray-600 bg-gray-50"
      }
    },
    purple: {
      gradient: "from-purple-500 to-purple-600",
      background: "bg-purple-50",
      icon: "text-purple-600",
      text: "text-purple-900",
      change: {
        positive: "text-emerald-600 bg-emerald-50",
        negative: "text-red-600 bg-red-50",
        neutral: "text-gray-600 bg-gray-50"
      }
    },
    orange: {
      gradient: "from-orange-500 to-orange-600",
      background: "bg-orange-50",
      icon: "text-orange-600",
      text: "text-orange-900",
      change: {
        positive: "text-emerald-600 bg-emerald-50",
        negative: "text-red-600 bg-red-50",
        neutral: "text-gray-600 bg-gray-50"
      }
    }
  };

  const scheme = colorSchemes[color] || colorSchemes.blue;

  // Animate the counter
  useEffect(() => {
    if (value === undefined || value === null) return;

    const startValue = 0;
    const endValue = parseFloat(value) || 0;
    const duration = animationDuration;
    const startTime = Date.now();

    const animateValue = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = startValue + (endValue - startValue) * easeOutQuart;

      setDisplayValue(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animateValue);
      } else {
        setIsLoaded(true);
      }
    };

    const timeout = setTimeout(() => {
      requestAnimationFrame(animateValue);
    }, 100);

    return () => clearTimeout(timeout);
  }, [value, animationDuration]);

  // Format the display value
  const formatValue = (val) => {
    if (format === "currency") {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(val);
    } else if (format === "percentage") {
      return `${val.toFixed(1)}%`;
    } else if (format === "decimal") {
      return val.toFixed(2);
    } else {
      return Math.round(val).toLocaleString();
    }
  };

  // Calculate percentage change
  const calculateChange = () => {
    if (!previousValue || previousValue === 0) return { value: 0, type: "neutral" };

    const change = ((value - previousValue) / previousValue) * 100;
    const type = change > 0 ? "positive" : change < 0 ? "negative" : "neutral";

    return { value: Math.abs(change), type };
  };

  const change = calculateChange();

  const getChangeIcon = () => {
    switch (change.type) {
      case "positive":
        return faArrowUp;
      case "negative":
        return faArrowDown;
      default:
        return faMinus;
    }
  };

  return (
    <div className={`relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${className}`}>
      {/* Gradient Background */}
      <div className={`absolute inset-0 bg-gradient-to-r ${scheme.gradient} opacity-5`}></div>

      {/* Card Content */}
      <div className={`relative bg-white border border-gray-100 rounded-xl p-6 ${scheme.background} bg-opacity-30`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-lg ${scheme.background}`}>
            <FontAwesomeIcon
              icon={icon}
              className={`text-xl ${scheme.icon}`}
            />
          </div>

          {/* Change Indicator */}
          {previousValue && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${scheme.change[change.type]}`}>
              <FontAwesomeIcon
                icon={getChangeIcon()}
                className="text-xs"
              />
              <span>{change.value.toFixed(1)}%</span>
            </div>
          )}
        </div>

        {/* Value */}
        <div className="mb-2">
          <div className={`text-3xl font-bold ${scheme.text} transition-all duration-500 ${isLoaded ? 'scale-100' : 'scale-110'}`}>
            {prefix}
            {formatValue(displayValue)}
            {suffix}
          </div>
        </div>

        {/* Title */}
        <div className="text-sm font-medium text-gray-600 uppercase tracking-wide">
          {title}
        </div>

        {/* Bottom gradient line */}
        <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${scheme.gradient} rounded-b-xl`}></div>

        {/* Animated loading bar */}
        {!isLoaded && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 rounded-b-xl overflow-hidden">
            <div className={`h-full bg-gradient-to-r ${scheme.gradient} animate-pulse`}></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnimatedKPICard;